import {
  Zone as InspectionZone,
  useUpdateZoneWorkFlowStatusMutation,
  useWorkflowStagesAndZonesQuery,
  ZonesForStructureDocument,
} from '@/__generated__/graphql';
import { sortWorkflowStages } from '@/components/ZonesManagementRevamped/utils';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import * as state from '@/state';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { DndContext, DragEndEvent, MouseSensor, useSensor, useSensors } from '@dnd-kit/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { ZoneDetailDialog } from '../../ZoneDetailDialog';
import { Stage } from './Stage';
import { Container, StageList, StageSkeleton } from './styles';

export const KanbanBoard = () => {
  const [stackWidth, setStackWidth] = useState<number>(0);
  const containerReference = useRef<HTMLDivElement>(null);
  const structureId = useGetStructureId();
  const [workflowStages, setWorkflowStages] = useAtom(state.workflowStages);
  const searchTerm = useAtomValue(state.searchedZone);
  const [zones, setZones] = useAtom(state.zones);
  const [selectedZones, setSelectedZones] = useState<InspectionZone[]>([]);
  const { showSnackBar } = useSnackBarMessage({});
  const [updateZoneWorkFlowStatus] = useUpdateZoneWorkFlowStatusMutation();
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 10,
    },
  });
  const sensors = useSensors(mouseSensor);
  const { loading } = useWorkflowStagesAndZonesQuery({
    variables: {
      structureId,
      searchTerm,
    },
    skip: !structureId,
    onCompleted: (data) => {
      if (data.structure?.workflowStages) {
        const sortedStages = sortWorkflowStages(
          data.structure.workflowStages?.filter(
            (stage): stage is NonNullable<typeof stage> => stage !== null
          )
        );
        setWorkflowStages(sortedStages);
      }
      if (data.zonesForStructure) {
        setZones(data.zonesForStructure);
      }
    },
  });

  useEffect(() => {
    const currentContainer = containerReference.current;

    const resizeObserver = new ResizeObserver(() => {
      if (currentContainer?.offsetWidth) setStackWidth(currentContainer.offsetWidth);
    });

    if (currentContainer) resizeObserver.observe(currentContainer);

    return () => {
      if (currentContainer) resizeObserver.unobserve(currentContainer);
    };
  }, [containerReference]);

  const stageWidth = useMemo(() => {
    if (stackWidth === 0) return 0;
    return `calc((${stackWidth}px - 12.6rem) / 4)`;
  }, [stackWidth]);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) return;
    const zoneId = active.id;
    const targetStageId = over.id;
    const draggedZone = zones.find((zone) => zone.id === zoneId);
    const targetStage = workflowStages.find((stage) => stage.id === targetStageId);

    if (!draggedZone || !targetStage) return;

    try {
      const isSelected = selectedZones?.some((zone) => zone.id === draggedZone.id);
      const zonesToMove =
        isSelected && selectedZones && selectedZones.length > 0 ? selectedZones : [draggedZone];
      if (
        zonesToMove.length === 1 &&
        draggedZone.workflowStatus?.workflowStageId === targetStage.id
      )
        return;
      const updatedZones = zones.map((zone) => {
        const shouldMove = zonesToMove.some((selectedZone) => selectedZone.id === zone.id);
        if (shouldMove) {
          return {
            ...zone,
            workflowStatus: {
              ...zone.workflowStatus,
              workflowStageId: targetStage.id,
              status: targetStage.name,
              assignedToUser: undefined,
            },
          };
        }
        return zone;
      });
      setZones(updatedZones);
      await updateZoneWorkFlowStatus({
        variables: {
          input: {
            structureId,
            zoneIds: zonesToMove.map((zone) => zone.id as string),
            workflowStageId: targetStage.id,
          },
        },
        refetchQueries: [ZonesForStructureDocument],
      });
      setSelectedZones([]);
    } catch (error) {
      console.error('Error updating zone status:', error);
      showSnackBar('Failed to update zone status', 'error');
    }
  };

  const handleClearSelection = (e: React.MouseEvent) => {
    if (!e.ctrlKey && selectedZones && selectedZones.length > 0) {
      setSelectedZones([]);
    }
  };

  return (
    <Container ref={containerReference} onClick={handleClearSelection}>
      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        <StageList>
          {loading &&
            Array.from({ length: 4 }, (_, index) => (
              <StageSkeleton key={index} width={stageWidth} animation="wave" />
            ))}

          {!loading &&
            workflowStages?.map((stage) => (
              <Stage
                key={stage?.id}
                details={stage}
                width={stageWidth}
                selectedZone={selectedZones ?? []}
                setSelectedZones={setSelectedZones}
              />
            ))}
        </StageList>
      </DndContext>
      <ZoneDetailDialog />
    </Container>
  );
};
